import { FieldProps } from "formik";
import { Autocomplete } from "formik-mui";
import {
  AutocompleteRenderInputParams,
  debounce,
  ListItem,
  ListItemText,
  TextField,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { ApiError, Benutzer, BenutzerListe } from "../../api";
import { useGetAngestellte, useGetBenutzer } from "../../client/hooks";

interface BenutzerSelectProps extends FieldProps {
  label: string;
  required?: boolean;
  fullWidth?: boolean;
  benutzerArraySetter?: (benutzerArray: Benutzer[]) => void;
}

function AngestelltenSelect({
  label,
  required,
  fullWidth,
  benutzerArraySetter,
  field,
  form,
  meta,
}: BenutzerSelectProps) {
  const getAngestellte = useGetAngestellte();
  const ladeBenutzer = useGetBenutzer();
  const [optionen, setOptionen] = useState<ReadonlyArray<Benutzer>>([]);
  const [inputValue, setInputValue] = useState(field.value);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (field.value && typeof field.value === "string") {
      ladeBenutzer(field.value)
        .then((benutzer) => {
          form.setFieldValue(field.name, benutzer);
        })
        .catch((error) => {
          console.error("Error loading user:", error);
        });
    }
  }, [field.value, field.name, form, ladeBenutzer]);

  const ladeOptionen = useMemo(
    () =>
      debounce(
        async (
          request: { input: string },
          callback: (error?: ApiError, results?: BenutzerListe) => void
        ) =>
          getAngestellte({
            search: request.input,
            page: 0,
            limit: 20,
            sort: "name",
          })
            .then(async (results) => {
              const priorityIds = JSON.parse(
                localStorage.getItem("letzteMitarbeiter") ?? "[]"
              );

              // Fetch all priority items corresponding to priorityIds
              const priorityItemsResults = await Promise.allSettled(
                priorityIds.map((id: string) => {
                  const existingItem = [...results.items].find(
                    (item) => item.id === id
                  );

                  return existingItem
                    ? Promise.resolve(existingItem)
                    : ladeBenutzer(id);
                })
              );

              const priorityItems = priorityItemsResults
                .filter((result) => result.status === "fulfilled")
                .map(
                  (result) => (result as PromiseFulfilledResult<Benutzer>).value
                )
                .filter(
                  (item: Benutzer) => item.mitarbeiterTyp === "ANGESTELLTER"
                );

              // Optionally filter priority items by input
              const filteredPriorityItems =
                request.input.trim() !== ""
                  ? priorityItems.filter((item: Benutzer) =>
                      item.name
                        .toLowerCase()
                        .includes(request.input.toLowerCase())
                    )
                  : priorityItems;
              // Remove duplicates from the final list
              const seenIds = new Set(
                filteredPriorityItems.map((item) => item.id)
              );
              const finalResults = [
                ...filteredPriorityItems,
                ...results.items.filter((item) => !seenIds.has(item.id)),
              ];
              if (benutzerArraySetter) {
                if (inputValue !== undefined) {
                  benutzerArraySetter(results.items);
                }
                callback(undefined, { ...results, items: results.items });
              } else {
                callback(undefined, { ...results, items: finalResults });
              }
            })
            .catch((error) => callback(error)),
        400
      ),
    [getAngestellte]
  );

  useEffect(() => {
    ladeOptionen({ input: searchValue }, (error, results) => {
      if (error) console.error("Error:", error);
      setOptionen([...(results ? results.items : [])]);
    });
  }, [searchValue, ladeOptionen]);

  return (
    <Autocomplete
      field={field}
      form={form}
      meta={meta}
      filterSelectedOptions={true}
      filterOptions={(x) => x} // Deaktiviert die Filterlogik, damit komplett nur in dem Backend gesucht werden kann.
      options={optionen}
      fullWidth={fullWidth}
      getOptionLabel={(option: string | Benutzer) => {
        if (typeof option === "string") {
          return "";
        } else {
          return [option.name, option.vorname].filter(Boolean).join(", ");
        }
      }}
      onChange={(event, value) => {
        setInputValue(value);
        form.setFieldValue(field.name, value);
        const benutzer = value as Benutzer;
        if (benutzer) {
          const benutzerId = value ? benutzer.id : "";
          if (benutzerId !== "") {
            let letzteMitarbeiter = JSON.parse(
              localStorage.getItem("letzteMitarbeiter") ?? "[]"
            );

            letzteMitarbeiter = letzteMitarbeiter.filter(
              (id: string) => id !== benutzerId
            );

            letzteMitarbeiter.unshift(benutzerId);

            if (letzteMitarbeiter.length > 10) {
              letzteMitarbeiter.splice(10);
            }

            localStorage.setItem(
              "letzteMitarbeiter",
              JSON.stringify(letzteMitarbeiter)
            );
          }
        }
      }}
      onInputChange={(event, value) => {
        setSearchValue(value);
      }}
      renderOption={(props, option: Benutzer) => {
        return (
          <ListItem {...props} key={option.id}>
            <ListItemText
              primary={[option.name, option.vorname].filter(Boolean).join(", ")}
              secondary={[
                option.personalNummer,
                option.email ?? option.geburtsDatum ?? null,
              ]
                .filter(Boolean)
                .join(", ")}
            />
          </ListItem>
        );
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField {...params} label={label + (required ? " *" : "")} />
      )}
    />
  );
}

export default AngestelltenSelect;
